import { FreeBusyScheduleForResourcesResponse } from "../../../createOrderApi"
import { ResourceFiltersSelectedItem, ResourceFiltersTypes } from "../../../createOrderTypes"
import { TimelineCalendarResource } from "../calendar/fullCalendarTypes"
import { filterResources, isAvailabilityViewFree } from "../helpers"

import { loadServiceCenterFilter } from "./timelineLocationFilterStorage"

export const SITTINGS_FILTER_IDENTIFIER = "sittings"
export const AVAILABILITY_VIEW_INTERVAL_MINUTES = 5
export const MEETING_WINDOW_DEBOUNCE_MS = 400
// Beyond a day the 5-minute availabilityView string grows unreasonably, and a multi-day booking is the
// book-period flow rather than an Outlook meeting.
export const MAX_AVAILABILITY_WINDOW_HOURS = 24

type InitialFiltersSeed = Readonly<{
    userId: number
    isOutlookFlow: boolean
    guestCount: number
    /** A recurring series is booked as a reserved period, so that mode starts on and stays on. */
    isRecurringBooking?: boolean
}>

// The saved locations have to be read synchronously: the chips seed from this state once, on mount.
export const createInitialFilters = ({
    userId,
    isOutlookFlow,
    guestCount,
    isRecurringBooking = false,
}: InitialFiltersSeed): ResourceFiltersTypes => {
    const hasGuestCount = isOutlookFlow && guestCount > 0

    return {
        filters: [
            {
                identifier: "resourceTypeIds",
                values: [
                    { key: "Room", value: 1 },
                    { key: "Av equipment", value: 2 },
                ],
            },
            ...(hasGuestCount
                ? [
                      {
                          identifier: SITTINGS_FILTER_IDENTIFIER,
                          isRemovable: true,
                          values: [{ key: guestCount, value: guestCount }],
                      },
                  ]
                : []),
            ...loadServiceCenterFilter(userId),
        ],
        search: "",
        isBookedPeriod: isRecurringBooking,
        onlyAvailableForMeeting: isOutlookFlow,
    }
}

/** Resources with no schedule entry are "unknown", not busy — a gap in the response must never hide a row. */
export const selectBusyResourceKeys = ({ resources }: FreeBusyScheduleForResourcesResponse): ReadonlySet<string> => {
    const busyKeys = new Set<string>()

    for (const item of resources ?? []) {
        const availability = item.availabilities?.find((slot) => slot.availabilityView)
        if (!availability || isAvailabilityViewFree(availability.availabilityView)) continue

        if (item.resource?.id !== undefined) busyKeys.add(String(item.resource.id))
        if (item.resource?.uid) busyKeys.add(item.resource.uid)
    }

    return busyKeys
}

type HiddenCountArgs = Readonly<{
    calendarResources: TimelineCalendarResource[]
    filters: ResourceFiltersSelectedItem[]
    searchRegex: RegExp | undefined
    activeResourceIds: readonly string[] | null
    visibleCount: number
}>

/** What the timeline would show without the Outlook-seeded narrowing, so the user is told how much is hidden. */
export const countHiddenByMeetingFit = ({
    calendarResources,
    filters,
    searchRegex,
    activeResourceIds,
    visibleCount,
}: HiddenCountArgs): number => {
    const withoutSeats = filters.filter((f) => f.identifier !== SITTINGS_FILTER_IDENTIFIER)
    const unnarrowed = filterResources(calendarResources, withoutSeats, searchRegex, activeResourceIds)

    return unnarrowed.length - visibleCount
}
